/* eslint-disable react/prop-types */
import Constants from '@/Constants';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { contentService } from '@/services/ContentService';
import { getFormatTime } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { Slider } from '@material-ui/core';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from '../Loader';
import VideoProcessingAlert from '../VideoProcessingAlert';
import './style.scss';

interface Props {
    content: Item;
    finishLesson(): void;
    startVideoLesson(): void;
    isLessonFinished: boolean;
}

const AudioAndVideoPlayer: React.FC<Props> = (props: Props) => {
    const { content, isLessonFinished, startVideoLesson, finishLesson } = props;
    const currentLanguage = useSelector((state: ApplicationState) => state.locale.data._LANGUAGE as LanguageType);

    const { currentContentLanguage, currentContentSubtitle } = useSelector((state: ApplicationState) => ({
        currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
        currentContentSubtitle: state.locale.currentContentSubtitle,
    }));

    const [currentContentPath, setCurrentContentPath] = useState<string>();
    const [currentContentSubtitlePath, setCurrentContentSubtitlePath] = useState<string>();
    const [playing, setPlaying] = useState<boolean>(false);
    const [played, setPlayed] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [formatedDuration, setFormatedDuration] = useState<string>('00:00');
    const [player, setPlayer] = useState<HTMLVideoElement | HTMLAudioElement>({} as HTMLVideoElement | HTMLAudioElement);
    const [fullScreen, setFullScreen] = useState<boolean>(false);
    const [showFullScreenControls, setShowFullScreenControls] = useState<boolean>(true);
    const [secondsToFadeOut, setSecondsToFadeOut] = useState<number>(0);
    const [videoMode, setVideoMode] = useState<boolean>(false);
    const [firstTimePlayed, setFirstTimePlayed] = useState<boolean>(false);
    const [buffering, setBuffering] = useState<boolean>(false);
    const SECONDS_MIN_TO_FADE_OUT = 5;

    const fullscreenListener = useCallback(() => {
        setFullScreen(!fullScreen);
        setShowFullScreenControls(!fullScreen);
        setSecondsToFadeOut(0);
    }, [fullScreen, setFullScreen]);

    const onPressVideoHandler = (): void => {
        setSecondsToFadeOut(0);

        if (fullScreen) {
            displayFullScreenControls(!showFullScreenControls);
        } else {
            playPause();
        }
    };

    const onPressPlayerHandler = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
        e.stopPropagation();
        setSecondsToFadeOut(0);
        playPause();
    };

    const onChangeSliderHandler = (e: ChangeEvent<{}>, value: number | number[]): void => {
        player.currentTime = (value as number) || player.currentTime;
        setPlayed(player.currentTime);
        setSecondsToFadeOut(0);
    };

    const onTimeUpdateHandler = (): void => {
        if (!firstTimePlayed) {
            setFirstTimePlayed(true);
            startVideoLesson();
        }
        const minimumDuration = duration * 0.7;

        if (player.currentTime >= minimumDuration && !isLessonFinished) {
            finishLesson();
        }

        setBuffering(playing && played === player.currentTime);
        setPlayed(player.currentTime);
    };

    const onEndedHandler = (): void => {
        setPlaying(false);
        setPlayed(0);
        player.currentTime = 0;
    };

    const showOrHideFullScreenHandler = (): void => {
        if (fullScreen) {
            document.exitFullscreen();
        } else {
            const videoContainer = document.getElementById('videoContainer');

            if (videoContainer !== null) {
                displayFullScreenControls(true);
                videoContainer.requestFullscreen();
            }
        }
    };

    const forwardHandler = (): void => {
        player.currentTime = player.currentTime + 10;
    };

    const backwardHandler = (): void => {
        player.currentTime = player.currentTime - 10;
    };

    const onLoadedMetadataHandler = (): void => {
        setPlaying(false);
        setDuration(player.duration);
        setFormatedDuration(getFormatTime(player.duration));
    };

    const playPause = (): void => {
        if (playing) {
            player.pause();
        } else {
            player.play();
        }
        setPlaying(!playing);
    };

    const inverseTimer = (): string => {
        return getFormatTime(duration - player.currentTime);
    };

    const displayFullScreenControls = (showControls: boolean): void => {
        const fullScreenControl = document.getElementById('fullScreenControl');

        if (fullScreenControl === null) {
            return;
        }

        if (showControls) {
            fullScreenControl.classList.remove('hide');
        } else {
            fullScreenControl.classList.add('hide');
        }

        setShowFullScreenControls(showControls);
    };

    useEffect(() => {
        const intervalToFadeOut = setInterval(() => {
            setSecondsToFadeOut((secondsToFadeOut) => secondsToFadeOut + 1);
        }, 1000);

        if (content.mediaType === 'VIDEO') {
            setVideoMode(true);
        }

        return () => {
            clearInterval(intervalToFadeOut);
        };
    }, [content.mediaType]);

    useEffect(() => {
        document.addEventListener('fullscreenchange', fullscreenListener, false);

        return () => {
            document.removeEventListener('fullscreenchange', fullscreenListener, false);
        };
    }, [fullscreenListener]);

    useEffect(() => {
        if (secondsToFadeOut >= SECONDS_MIN_TO_FADE_OUT) {
            setShowFullScreenControls(false);
            setSecondsToFadeOut(0);
        }
    }, [secondsToFadeOut]);

    useEffect(() => {
        displayFullScreenControls(showFullScreenControls);
    }, [showFullScreenControls]);

    const loadNewSourceByNewLanguage = useCallback(() => {
        setCurrentContentPath(contentService.getContentPathByLanguageType(content, currentContentLanguage || currentLanguage));
        setCurrentContentSubtitlePath(contentService.getContentSubtitlePathByLanguageType(content, currentContentSubtitle || currentLanguage));
        if (player && player.load) {
            player.load();
        }
    }, [content, currentContentLanguage, currentContentSubtitle, currentLanguage, player]);

    useEffect(loadNewSourceByNewLanguage, [content.path, currentContentLanguage]);

    const getPlayer = () => {
        if (videoMode) {
            const contentPath = currentContentPath?.substring(0, currentContentPath.lastIndexOf('.'));
            return (
                <>
                    <VideoProcessingAlert contentId={Number(content.id)} contentPath={String(currentContentPath)} />

                    {buffering && (
                        <div className={`lms-media-player__loader ${fullScreen ? 'fullscreen' : ''}`}>
                            <Loader />
                        </div>
                    )}

                    <video
                        className={`media-player__video ${fullScreen ? 'fullscreen-video' : 'default-video'}`}
                        onClick={onPressVideoHandler}
                        onEnded={onEndedHandler}
                        onLoadedMetadata={onLoadedMetadataHandler}
                        onTimeUpdate={onTimeUpdateHandler}
                        ref={(playerRef) => {
                            setPlayer(playerRef as HTMLVideoElement);
                        }}
                    >
                        <source src={`${contentPath}.webm`} type="video/webm" />
                        <source src={`${contentPath}.mp4`} type="video/mp4" />

                        {currentContentSubtitlePath && (
                            <track
                                default
                                kind="subtitles"
                                src={currentContentSubtitlePath}
                                srcLang={currentContentSubtitle || currentLanguage}
                            ></track>
                        )}
                    </video>
                </>
            );
        }

        return (
            <audio
                className="media-player__video"
                onClick={onPressVideoHandler}
                onEnded={onEndedHandler}
                onLoadedMetadata={onLoadedMetadataHandler}
                onTimeUpdate={onTimeUpdateHandler}
                preload="metadata"
                ref={(playerRef) => {
                    setPlayer(playerRef as HTMLAudioElement);
                }}
            >
                <source src={currentContentPath}></source>
            </audio>
        );
    };

    return (
        <div>
            {currentContentPath && (
                <div className="lms-media-player">
                    <div className="media-player__screen" id="videoContainer">
                        {fullScreen && (
                            <div className="fullscreen-controls" id="fullScreenControl" onClick={onPressVideoHandler}>
                                <div>
                                    {playing && (
                                        <span className="material-icons fullscreen-controls__controls-pause" onClick={(e) => onPressPlayerHandler(e)}>
                                            {Constants.icons.PAUSE_CIRCLE_FILLED}
                                        </span>
                                    )}
                                    {!playing && (
                                        <span className="material-icons fullscreen-controls__controls-play" onClick={(e) => onPressPlayerHandler(e)}>
                                            {Constants.icons.PLAY_CIRCLE_FILLED}
                                        </span>
                                    )}
                                </div>
                                <div className="fullscreen-controls__progress">
                                    <div className="fullscreen-controls__progress-bar">
                                        <Slider
                                            classes={{
                                                root: 'slider-root',
                                                rail: 'slider-rail-fullscreen',
                                                track: 'slider-track',
                                                thumb: 'slider-thumb',
                                            }}
                                            defaultValue={0}
                                            max={duration}
                                            min={0}
                                            onChange={(event, value) => onChangeSliderHandler(event, value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onTimeUpdate={onTimeUpdateHandler}
                                            value={played}
                                        />
                                        <div className="fullscreen-controls__time-current">{formatedDuration}</div>
                                        <div className="fullscreen-controls__time-left">{inverseTimer()}</div>
                                    </div>
                                    <div>
                                        <span className="material-icons fullscreen-controls__exit" onClick={showOrHideFullScreenHandler}>
                                            {Constants.icons.FULLSCREEN_EXIT}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {getPlayer()}
                    </div>

                    <div className="media-player__progress">
                        <Slider
                            classes={{
                                root: 'slider-root',
                                rail: 'slider-rail',
                                track: 'slider-track',
                                thumb: 'slider-thumb',
                            }}
                            defaultValue={0}
                            max={duration}
                            min={0}
                            onChange={(event, value) => onChangeSliderHandler(event, value)}
                            onTimeUpdate={onTimeUpdateHandler}
                            value={played}
                        />

                        <div className="media-player__time-current">{formatedDuration}</div>
                        <div className="media-player__time-left">{inverseTimer()}</div>
                    </div>

                    <div className="media-player__controls">
                        <span className="material-icons media-player__controls-replay" onClick={backwardHandler}>
                            {Constants.icons.REPLAY_10}
                        </span>
                        {playing && (
                            <span className="material-icons media-player__controls-pause" onClick={(e) => onPressPlayerHandler(e)}>
                                {Constants.icons.PAUSE_CIRCLE_FILLED}
                            </span>
                        )}
                        {!playing && (
                            <span className="material-icons media-player__controls-play" onClick={(e) => onPressPlayerHandler(e)}>
                                {Constants.icons.PLAY_CIRCLE_FILLED}
                            </span>
                        )}
                        <span className="material-icons media-player__controls-forward" onClick={forwardHandler}>
                            {Constants.icons.FORWARD_10}
                        </span>
                        {videoMode && (
                            <span className="material-icons media-player__controls-fullscreen" onClick={showOrHideFullScreenHandler}>
                                {Constants.icons.FULLSCREEN}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioAndVideoPlayer;
